import React, { useState } from "react";
import { getStatusColors, formatRate } from "./performanceUtils";

function PerformanceQuadrant({ data }) {
  const [hovered, setHovered] = useState(null);

  if (data.length === 0) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 dark:shadow-none">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Effort vs Output</h3>
        <p className="mt-4 text-center text-sm text-gray-400 dark:text-slate-400">No performance data available.</p>
      </section>
    );
  }

  const maxEffort = Math.max(...data.map((u) => u.totalEffort), 1);
  const maxOutput = Math.max(...data.map((u) => u.totalOutput), 1);
  const avgEffort = data[0]?.avgEffort ?? 0;
  const avgOutput = data[0]?.avgOutput ?? 0;

  // avgX = % from left; avgY = % from bottom → top = (100 - avgY)%
  const avgX = Math.min(Math.max((avgEffort / maxEffort) * 100, 12), 88);
  const avgY = Math.min(Math.max((avgOutput / maxOutput) * 100, 12), 88);
  const avgYTop = 100 - avgY;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 dark:shadow-none">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Effort vs Output</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
        Each dot is one assignee. Dashed lines mark team averages. Hover a dot for details.
      </p>

      <div className="mt-5 flex gap-3">
        {/* Y-axis label */}
        <div className="flex items-center justify-center w-5 flex-shrink-0">
          <span
            className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-400 select-none"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Output → Screener Responses
          </span>
        </div>

        {/* Chart */}
        <div className="flex-1 min-w-0">
          {/* Wrapper: relative, no overflow restriction so tooltips can escape */}
          <div className="relative h-[380px]">

            {/* Layer 1 — background (clipped to rounded border) */}
            <div className="absolute inset-0 rounded-xl border border-gray-200 overflow-hidden bg-white dark:border-slate-700 dark:bg-slate-900">
              {/* Quadrant fills */}
              <div className="absolute bg-purple-50 dark:bg-purple-500/10" style={{ left: 0, top: 0, width: `${avgX}%`, height: `${avgYTop}%` }} />
              <div className="absolute bg-green-50 dark:bg-green-500/10"  style={{ left: `${avgX}%`, top: 0, right: 0, height: `${avgYTop}%` }} />
              <div className="absolute bg-red-50 dark:bg-red-500/10"    style={{ left: 0, top: `${avgYTop}%`, width: `${avgX}%`, bottom: 0 }} />
              <div className="absolute bg-orange-50 dark:bg-orange-500/10" style={{ left: `${avgX}%`, top: `${avgYTop}%`, right: 0, bottom: 0 }} />

              {/* Avg divider lines */}
              <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-gray-400 opacity-70 dark:border-slate-500" style={{ left: `${avgX}%` }} />
              <div className="absolute left-0 right-0 border-t-2 border-dashed border-gray-400 opacity-70 dark:border-slate-500" style={{ top: `${avgYTop}%` }} />

              {/* Avg line labels */}
              <span className="absolute rounded bg-white/90 dark:bg-slate-800/90 dark:shadow-black/30 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:text-slate-400 shadow-sm" style={{ left: `${avgX}%`, top: 6, transform: "translateX(-50%)" }}>
                avg effort
              </span>
              <span className="absolute rounded bg-white/90 dark:bg-slate-800/90 dark:shadow-black/30 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:text-slate-400 shadow-sm" style={{ top: `${avgYTop}%`, right: 8, transform: "translateY(-50%)" }}>
                avg output
              </span>

              {/* Quadrant corner labels */}
              <div className="absolute left-3 top-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-purple-400 flex-shrink-0" />
                <span className="text-[11px] font-bold text-purple-700 dark:text-purple-300">Efficient</span>
              </div>
              <div className="absolute right-3 top-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-400 flex-shrink-0" />
                <span className="text-[11px] font-bold text-green-700 dark:text-green-300">Top Performer</span>
              </div>
              <div className="absolute left-3 bottom-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-[11px] font-bold text-red-700 dark:text-red-300">Unacceptable Output</span>
              </div>
              <div className="absolute right-3 bottom-3 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-orange-400 flex-shrink-0" />
                <span className="text-[11px] font-bold text-orange-700 dark:text-orange-300">Underdelivering</span>
              </div>
            </div>

            {/* Layer 2 — dots + tooltips (no overflow clipping) */}
            <div className="absolute inset-0">
              {data.map((user) => {
                const xPct = Math.min(Math.max((user.totalEffort / maxEffort) * 100, 5), 95);
                const yPct = Math.min(Math.max((user.totalOutput / maxOutput) * 100, 5), 95);
                const colors = getStatusColors(user.status);
                const isHovered = hovered?.userId === user.userId;

                // Flip tooltip to stay visible: right half → show left; top half → anchor to bottom
                const flipH = xPct > 60;
                const flipV = yPct > 60;

                return (
                  <div
                    key={user.userId}
                    className="absolute"
                    style={{
                      left: `${xPct}%`,
                      bottom: `${yPct}%`,
                      transform: "translate(-50%, 50%)",
                      zIndex: isHovered ? 50 : 10,
                    }}
                    onMouseEnter={() => setHovered(user)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    {/* Dot */}
                    <div
                      className="cursor-pointer rounded-full border-2 border-white shadow-lg dark:border-slate-900 dark:shadow-black/40 transition-all duration-150"
                      style={{
                        backgroundColor: colors.dot,
                        width: isHovered ? 24 : 18,
                        height: isHovered ? 24 : 18,
                      }}
                    />

                    {/* Tooltip */}
                    {isHovered && (
                      <div
                        className="pointer-events-none absolute z-50 w-52 rounded-xl bg-gray-900 p-3 text-xs text-white shadow-2xl dark:bg-slate-950 dark:ring-1 dark:ring-slate-700"
                        style={{
                          ...(flipH ? { right: "calc(100% + 10px)" } : { left: "calc(100% + 10px)" }),
                          ...(flipV ? { bottom: 0 } : { top: 0 }),
                        }}
                      >
                        <p className="truncate text-[11px] font-semibold text-gray-300">{user.user?.email}</p>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Effort</span>
                            <span className="font-bold text-white">{user.totalEffort}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Output</span>
                            <span className="font-bold text-white">{user.totalOutput}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Rate</span>
                            <span className="font-bold text-white">{formatRate(user.conversionRate)}</span>
                          </div>
                        </div>
                        <div className="mt-2.5 border-t border-white/10 pt-2">
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                            style={{ backgroundColor: `${colors.dot}30`, color: colors.dot }}
                          >
                            {user.status}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* X-axis label */}
          <p className="mt-2 text-center text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-400 select-none">
            Effort → Total Outreach Activity
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 border-t border-gray-100 dark:border-slate-700 pt-4">
        {data.map((user) => {
          const colors = getStatusColors(user.status);
          return (
            <div key={user.userId} className="flex items-center gap-2 text-xs">
              <span
                className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                style={{ backgroundColor: colors.dot }}
              />
              <span className="max-w-[160px] truncate text-gray-700 dark:text-slate-300">{user.user?.email}</span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${colors.bg} ${colors.text} ${colors.border}`}
              >
                {user.status}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default PerformanceQuadrant;
